import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Copy, WhatsappLogo, Check } from "@phosphor-icons/react";
import { fmtAmount } from "@/lib/util";

// Groups the provided payment rows by brand, formats a WhatsApp-friendly
// message with per-brand subtotals + grand total, and exposes a single-click
// Copy button. Brand & currency aware — separate lines per currency inside
// each brand when the mix is heterogeneous.
export default function WhatsAppExport({ open, onOpenChange, rows = [], defaultTitle = "Entries Pending" }) {
  const [title, setTitle] = useState(defaultTitle);
  const [copied, setCopied] = useState(false);

  const message = useMemo(() => {
    if (!rows.length) return "";
    // Explode multi-brand payments into per-brand pseudo-rows so the split
    // is honoured in the WhatsApp breakdown. Each allocation becomes its own
    // line item under the matching brand.
    const exploded = [];
    for (const r of rows) {
      const allocs = r.brand_allocations || [];
      if (allocs.length > 1) {
        for (const a of allocs) {
          exploded.push({
            ...r,
            brand_id: a.brand_id,
            brand_name: a.brand_name,
            net_amount: a.amount,
            vendor_name: `${r.vendor_name}${a.note ? ` (${a.note})` : ""}`,
          });
        }
      } else {
        exploded.push(r);
      }
    }
    // Group by brand → within each brand, sub-group by expense_type, and
    // within each type track per-currency subtotals. Result structure:
    //   Map<brand, Map<expenseType, rows[]>>
    const byBrand = new Map();
    for (const r of exploded) {
      const b = r.brand_name || "Unassigned brand";
      const t = r.expense_type || "Other";
      if (!byBrand.has(b)) byBrand.set(b, new Map());
      const brandTypes = byBrand.get(b);
      if (!brandTypes.has(t)) brandTypes.set(t, []);
      brandTypes.get(t).push(r);
    }
    const lines = [];
    lines.push(`*${title.trim() || "Entries"}*`);
    lines.push("");

    const grandTotals = {};   // { INR: 12000, USDT: 500 }
    for (const [brand, typesMap] of byBrand) {
      lines.push(`*${brand}*`);
      const brandTotals = {};
      let itemIdx = 0;
      for (const [type, typeRows] of typesMap) {
        lines.push(`_${type}_`);
        typeRows.forEach((r) => {
          itemIdx += 1;
          lines.push(`${itemIdx}. ${fmtAmount(r.net_amount, r.currency_code)} ${r.currency_code}, ${r.vendor_name}`);
          brandTotals[r.currency_code] = (brandTotals[r.currency_code] || 0) + (r.net_amount || 0);
          grandTotals[r.currency_code] = (grandTotals[r.currency_code] || 0) + (r.net_amount || 0);
        });
        // Per-expense-type subtotal (only when >1 type in this brand)
        if (typesMap.size > 1) {
          const typeTotals = {};
          for (const r of typeRows) {
            typeTotals[r.currency_code] = (typeTotals[r.currency_code] || 0) + (r.net_amount || 0);
          }
          const typeStr = Object.entries(typeTotals)
            .map(([cur, val]) => `${fmtAmount(val, cur)} ${cur}`)
            .join(" · ");
          lines.push(`   _Sub for ${type}: ${typeStr}_`);
        }
        lines.push("");
      }
      const subtotalStr = Object.entries(brandTotals)
        .map(([cur, val]) => `${fmtAmount(val, cur)} ${cur}`)
        .join(" · ");
      lines.push(`Sub total for ${brand}: ${subtotalStr}`);
      lines.push("");
    }
    lines.push(`*Grand Total: ${Object.entries(grandTotals).map(([cur, val]) => `${fmtAmount(val, cur)} ${cur}`).join(" · ")}*`);
    return lines.join("\n");
  }, [rows, title]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      toast.success("Message copied — paste into WhatsApp");
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) {
      toast.error("Copy failed — select the text manually");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="wa-dialog" className="max-w-lg rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <WhatsappLogo weight="fill" className="text-emerald-600" size={20} />
            WhatsApp message · {rows.length} row{rows.length === 1 ? "" : "s"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold">Title</Label>
            <Input
              data-testid="wa-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1"
              placeholder="Entries Pending"
            />
          </div>

          {rows.length === 0 ? (
            <div className="text-sm text-slate-500 py-4 text-center">
              No rows to export — pick some in the list above.
            </div>
          ) : (
            <div>
              <Label className="text-xs font-semibold">Preview</Label>
              <pre
                data-testid="wa-preview"
                className="mt-1 whitespace-pre-wrap font-mono text-xs bg-emerald-50/60 border border-emerald-200 rounded-xl p-3 max-h-[45vh] overflow-y-auto text-slate-800"
              >
                {message}
              </pre>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button
            data-testid="wa-copy"
            disabled={!message}
            onClick={copy}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {copied ? <><Check weight="bold" className="mr-2" size={14} /> Copied</> : <><Copy weight="bold" className="mr-2" size={14} /> Copy for WhatsApp</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
